import {
    Addon,
    Container as OverviewContainer,
    RemovedIngredient as OverviewRemovedIngredient,
    ProductItem,
} from '@/features/common/components/Overview/parts/ProductItem/types'
import {
    formatAddonName,
    formatProductItemName,
} from '@/features/common/components/Overview/utils/formats'
import {
    AddedItem,
    Container,
    ContainerItem,
    Item,
    RemovedIngredient,
    Topping,
} from '@/features/orders/types/app'
import { ItemsPriceCalculator } from '@/services/calculators/itemsPriceCalculator'
import { calculateTotalPriceFromUnitPrice } from '@/utils/calculateTotalPriceFromUnitPrice'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { TFunction } from 'i18next'
import invariant from 'tiny-invariant'

const calculateQuantityPerMainItemUnit = (quantity: number, mainItemQuantity: number): number => {
    if (quantity === 0) return 0

    invariant(mainItemQuantity > 0, 'Main item quantity must be greater than zero.')

    return quantity / mainItemQuantity
}

const transformToppingToAddon = (topping: Topping, t: TFunction<'app'>): Addon => ({
    id: topping.id,
    name: formatAddonName(
        topping.name,
        topping.quantity,
        formatFreeOfChargeAblePrice(topping.price, t)
    ),
})

const transformAddedItemToAddon = (
    addedItem: AddedItem,
    t: TFunction<'app'>,
    mainItemQuantity: number
): Addon => {
    const quantityPerMainItem = calculateQuantityPerMainItemUnit(
        addedItem.quantity,
        mainItemQuantity
    )

    return {
        id: addedItem.id,
        name: formatAddonName(
            addedItem.name,
            quantityPerMainItem,
            formatFreeOfChargeAblePrice(addedItem.price, t)
        ),
    }
}

const transformRemovedIngredient = (
    removedIngredient: RemovedIngredient
): OverviewRemovedIngredient => ({
    id: removedIngredient.id,
    name: removedIngredient.name,
})

const transformContainer = (
    container: Container,
    quantityPerMainItem: number,
    totalQuantity: number,
    t: TFunction<'app'>
): OverviewContainer => ({
    id: container.id,
    name: formatAddonName(
        container.name ?? t('common.overview.containerNameFallback'),
        quantityPerMainItem,
        formatFreeOfChargeAblePrice(container.price, t)
    ),
    price: formatFreeOfChargeAblePrice(
        calculateTotalPriceFromUnitPrice(container.price, totalQuantity),
        t,
        false,
        2
    ),
})

const transformContainers = (item: Item, t: TFunction<'app'>): OverviewContainer[] => [
    ...(item.container ? [transformContainer(item.container, 1, item.quantity, t)] : []),
    ...(item.addedItems?.flatMap((addedItem) =>
        addedItem.container
            ? [
                  transformContainer(
                      addedItem.container,
                      calculateQuantityPerMainItemUnit(addedItem.quantity, item.quantity),
                      addedItem.quantity,
                      t
                  ),
              ]
            : []
    ) ?? []),
]

export const transformItem = (item: Item, t: TFunction<'app'>): ProductItem => ({
    id: item.id,
    name: formatProductItemName(item.name, formatFreeOfChargeAblePrice(item.price, t)),
    quantity: item.quantity,
    price: formatFreeOfChargeAblePrice(
        ItemsPriceCalculator.calculateTotalPriceForItem(item),
        t,
        false,
        2
    ),
    addons: [
        ...(item.toppings?.map((topping) => transformToppingToAddon(topping, t)) ?? []),
        ...(item.addedItems?.map((addedItem) =>
            transformAddedItemToAddon(addedItem, t, item.quantity)
        ) ?? []),
    ],
    containers: transformContainers(item, t),
    removedIngredients: item.removedIngredients?.map(transformRemovedIngredient),
})

export const transformContainerItem = (
    containerItem: ContainerItem,
    t: TFunction<'app'>
): ProductItem => ({
    id: containerItem.id,
    name: formatProductItemName(
        containerItem.name ?? t('common.overview.containerNameFallback'),
        formatFreeOfChargeAblePrice(containerItem.price, t)
    ),
    quantity: containerItem.quantity,
    price: formatFreeOfChargeAblePrice(
        calculateTotalPriceFromUnitPrice(containerItem.price, containerItem.quantity),
        t,
        false,
        2
    ),
})
